import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, XCircle, Loader2, Radio, Download, X } from 'lucide-react';
import { Button, Pill, Card } from '../../index';
import { ConnectionStatus } from '../ConnectionStatus';
import { PolicyPipeline } from '../PolicyPipeline';
import { AgentPickerDropdown } from '../AgentPickerDropdown';
import { API_URL, getLiveTrustSnapshot, getMostActiveAgents } from '../../../../services/api';
import { publishedBandStyle, displayScore } from '../../../../services/trustBands';

/**
 * Cross-tab selection memory. The Trust Center mounts each tab only while
 * it is active (`topTab === 'live' && <LiveExecutionTab/>`), so switching
 * tabs unmounts this component and would otherwise wipe the operator's agent
 * selection. This module-level cache survives the unmount/remount cycle for
 * the lifetime of the page, restoring the picked agents (and their resolved
 * names) when the operator comes back to the tab.
 */
const SELECTION_CACHE = { selected: [], focused: null, names: {} };

/**
 * LiveExecutionTab — "Live Trust Action" (The Crossing, Slice D).
 *
 * Re-UX of the former Live Execution Center. Watch up to 3 running agents' live
 * Trust Score (the Prove7 Agent Trust Score™ ring) alongside the governed
 * execution feed. The ring + autonomy ladder + score inputs read the REAL
 * scoring config via GET /trust/profiles/{id}/live; the score animates from the
 * `trust_update` SSE channel as conformance is scored each run.
 *
 * @param {Array} props.enriched — scope-filtered agents (picker source)
 */
const MAX_AGENTS = 3;
/* Autonomy tier → the shared score ramp, so this dial reads the same as the Trust Score and
 * the inventory. It was a navy/steel ramp of its own: four shades that said which rung an agent
 * sat on but nothing about whether that was good, and nothing a colour-blind reader could rank.
 *
 * Tokens, not hexes, so the dial follows the theme. The hexes here stayed light-mode navy on a
 * dark surface, and they could not be changed without finding every copy. */
const TIER_COLOR = {
  'Self-Autonomous': 'var(--v2-band-strong)',
  'Supervised':      'var(--v2-band-adequate)',
  'Restricted':      'var(--v2-band-limited)',
  'Probation':       'var(--v2-band-critical)',
  'Unrated':         'var(--v2-band-unrated)',
};
/* The darker step, for the tier label and any band-coloured type. The ring colour is tuned for an
 * 11px stroke and does not carry enough contrast at 12px text. */
const TIER_INK = {
  'Self-Autonomous': 'var(--v2-band-strong-ink)',
  'Supervised':      'var(--v2-band-adequate-ink)',
  'Restricted':      'var(--v2-band-limited-ink)',
  'Probation':       'var(--v2-band-critical-ink)',
  'Unrated':         'var(--v2-band-unrated-ink)',
};

export function LiveExecutionTab({ enriched, focusAgentId, onFocusAgent }) {
  const agents = Array.isArray(enriched) ? enriched : [];

  // Resolved-name overrides: a picked agent (e.g. a Top-10-active one) may not
  // live in the scope-filtered `agents` list, so we remember the name we knew
  // at pick-time. Lookup order: scope list → override map → short-id fallback.
  const [nameOverrides, setNameOverrides] = useState(() => ({ ...SELECTION_CACHE.names }));
  const nameOf = useCallback(
    (id) => agents.find((a) => String(a.id) === String(id))?.name
      || nameOverrides[String(id)]
      || String(id).slice(0, 8),
    [agents, nameOverrides],
  );

  const [selected, setSelected] = useState(() => SELECTION_CACHE.selected); // up to 3 agent ids
  const [focused, setFocused] = useState(() => SELECTION_CACHE.focused);

  // Persist selection + resolved names across tab unmount/remount.
  useEffect(() => {
    SELECTION_CACHE.selected = selected;
    SELECTION_CACHE.focused = focused;
    SELECTION_CACHE.names = nameOverrides;
  }, [selected, focused, nameOverrides]);
  const [snapshots, setSnapshots] = useState({});        // id -> snapshot
  const [liveScores, setLiveScores] = useState({});      // id -> latest score (SSE)
  const [mostActive, setMostActive] = useState([]);
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState('disconnected');
  const [lastEventAt, setLastEventAt] = useState(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const feedRef = useRef(null);

  /* Seed from the shared cross-tab focus (so the agent picked in another Trust
   * tab shows here too), else the first scope agent. If a focus was chosen
   * elsewhere and isn't in the current selection, surface it without nuking the
   * existing comparison set. */
  useEffect(() => {
    if (selected.length === 0) {
      const seed = (focusAgentId && agents.find((a) => String(a.id) === String(focusAgentId)))
        ? String(focusAgentId)
        : (agents[0] ? String(agents[0].id) : null);
      if (seed) { setSelected([seed]); setFocused(seed); }
      return;
    }
    if (focusAgentId && !selected.includes(String(focusAgentId))) {
      setSelected((s) => [String(focusAgentId), ...s].slice(0, MAX_AGENTS));
      setFocused(String(focusAgentId));
    }
  }, [agents, selected, focusAgentId]);

  /* Fetch the Top-10 most-active list for the "Add" menu. */
  useEffect(() => {
    let alive = true;
    getMostActiveAgents(10, 30).then((res) => {
      if (alive && res && res.ok !== false) setMostActive(res.data || res || []);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  /* Fetch a snapshot for any selected agent we don't have yet. */
  useEffect(() => {
    selected.forEach((id) => {
      if (snapshots[id] !== undefined) return;
      getLiveTrustSnapshot(id).then((res) => {
        const data = res && res.ok !== false ? (res.data || res) : null;
        setSnapshots((s) => ({ ...s, [id]: data }));
        // Resolve the human-readable name from the snapshot so a selected agent
        // that isn't in the current scope filter (e.g. the System Policy
        // Enforcement SysLet under an ExteLets filter) never falls back to a raw
        // UUID slice ("00000000…"). UI display conventions: names over UUIDs.
        if (data && data.agentName) {
          setNameOverrides((m) => (m[String(id)] === data.agentName ? m : { ...m, [String(id)]: data.agentName }));
        }
      }).catch(() => setSnapshots((s) => ({ ...s, [id]: null })));
    });
  }, [selected, snapshots]);

  /* One tenant-wide SSE connection; filter to selected agents client-side. */
  useEffect(() => {
    let aborted = false;
    setStatus('connecting');
    const ctrl = new AbortController();
    (async () => {
      let token;
      try {
        const { getHeaders } = await import('../../../../services/api');
        const headers = await getHeaders();
        token = headers.Authorization || headers.authorization;
      } catch (_) { /* anonymous */ }
      try {
        const resp = await fetch(`${API_URL}/api/v1/trust/live-stream`, {
          method: 'GET',
          headers: { Accept: 'text/event-stream', ...(token ? { Authorization: token } : {}) },
          signal: ctrl.signal,
        });
        if (!resp.ok || !resp.body) { if (!aborted) setStatus('disconnected'); return; }
        if (!aborted) setStatus('connected');
        const reader = resp.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buf = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            const chunk = buf.slice(0, idx);
            buf = buf.slice(idx + 2);
            const parsed = parseSseChunk(chunk);
            if (parsed && !aborted) handleEvent(parsed);
          }
        }
        if (!aborted) setStatus('disconnected');
      } catch (e) {
        if (e?.name !== 'AbortError' && !aborted) setStatus('disconnected');
      }
    })();
    return () => { aborted = true; try { ctrl.abort(); } catch (_) { /* noop */ } };
  }, []);

  useEffect(() => {
    if (!autoScroll || !feedRef.current) return;
    feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [events.length, autoScroll]);

  const handleEvent = useCallback((evt) => {
    setLastEventAt(new Date());
    const { event, data } = evt;
    if (event === 'connected' || event === 'stats') return;
    if (event === 'trust_update' && data?.agentId) {
      setLiveScores((m) => ({ ...m, [data.agentId]: Number(data.newScore) }));
      return;
    }
    /* WS-0 — a prove was sealed (journey-time or runtime). Show it in the live
     * feed so operators see the agent walking the seven proves, not only live
     * calls. Each maps to one TrustAssertion (domain · predicate). */
    if (event === 'prove_sealed' && data?.agentId) {
      const label = `${data.domain || 'PROVE'} · ${data.predicate || 'sealed'}`;
      setEvents((es) => boundedAppend(es, {
        id: `prove-${data.correlationId || Date.now()}-${data.domain || ''}-${Math.random().toString(36).slice(2, 6)}`,
        type: 'prove', agentId: data.agentId, agentName: data.agentName,
        prove: label, skillName: data.predicate, decision: 'SEALED', stages: [],
        startedAt: data.timestamp || new Date().toISOString(),
        completedAt: data.timestamp || new Date().toISOString(),
      }));
      return;
    }
    if (event === 'execution_start') {
      const id = data.executionId || data.id || `exec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setEvents((es) => boundedAppend(es, {
        id, type: data.type || 'gel', agentId: data.agentId, agentName: data.agentName,
        startedAt: data.timestamp || new Date().toISOString(),
        skillName: data.skillName, stages: [], decision: 'EXECUTING', toolName: data.toolName,
      }));
      return;
    }
    if (event === 'phase_update' || event === 'policy_gate') {
      const id = data.executionId;
      setEvents((es) => es.map((e) => {
        if (e.id !== id) return e;
        const name = data.phase || data.stage;
        if (e.stages.some((s) => s.name === name)) return e;
        return { ...e, stages: [...e.stages, { name, result: data.result || 'passed', violation: data.violation, ruleName: data.ruleName, scope: data.scope }] };
      }));
      return;
    }
    if (event === 'execution_complete') {
      const id = data.executionId;
      setEvents((es) => es.map((e) => e.id === id ? {
        ...e, decision: data.decision || data.outcome || 'ALLOWED',
        completedAt: data.timestamp || new Date().toISOString(),
        duration: data.duration || data.durationMs,
        conformance: data.conformance, warnings: data.warnings || 0,
      } : e));
      return;
    }
    if (event === 'mcp_invocation') {
      setEvents((es) => boundedAppend(es, {
        id: data.invocationId || `mcp-${Date.now()}`, type: 'mcp', agentId: data.agentId, agentName: data.agentName,
        startedAt: data.timestamp || new Date().toISOString(), toolName: data.toolName,
        decision: data.decision || 'ALLOWED', duration: data.duration,
        stages: [{ result: data.decision === 'BLOCKED' ? 'blocked' : 'passed' }],
      }));
    }
  }, []);

  const addAgent = (id, name) => {
    if (!id) return;
    const key = String(id);
    if (name) setNameOverrides((m) => (m[key] === name ? m : { ...m, [key]: name }));
    setSelected((s) => (s.includes(key) || s.length >= MAX_AGENTS ? s : [...s, key]));
    setFocused(key);
    onFocusAgent?.(key);   // propagate to the other Trust tabs
  };
  const removeAgent = (id) => {
    setSelected((s) => {
      const next = s.filter((x) => x !== id);
      if (focused === id) setFocused(next[0] || null);
      return next;
    });
  };

  const focusedSnap = focused ? snapshots[focused] : null;
  const focusedScore = focused != null && liveScores[focused] != null
    ? liveScores[focused]
    : (focusedSnap?.score != null ? Number(focusedSnap.score) : null);

  const selectableAgents = agents.filter((a) => !selected.includes(String(a.id)));
  const feedEvents = events.filter((e) => selected.length === 0 || selected.includes(String(e.agentId)));

  /* The "Top N active" list comes from a tenant-wide endpoint that is NOT
   * scope-aware, so under the Lets / ExteLets toggle it would still offer
   * out-of-scope agents (e.g. Lets while the ExteLets filter is active).
   * `agents` is already the scope-filtered set from the parent, so intersect
   * the most-active list with it to honour the active toggle. */
  const scopeIds = useMemo(() => new Set(agents.map((a) => String(a.id))), [agents]);
  const topActiveItems = useMemo(() => mostActive
    .filter((m) => scopeIds.has(String(m.agentId)) && !selected.includes(String(m.agentId)))
    .map((m) => ({
      id: String(m.agentId),
      name: m.name || String(m.agentId).slice(0, 8),
      sub: `${m.runCount} runs`,
    })), [mostActive, scopeIds, selected]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10.5px] font-mono-v2 tracking-wider text-v2-ink-muted">TRUST · LIVE TRUST ACTION · LIVE</span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-v2-success-strong">
          <Radio size={12} className={status === 'connected' ? 'text-v2-success-strong' : 'text-v2-ink-muted'} />
          {status}
        </span>
        <ConnectionStatus status={status} lastEventAt={lastEventAt} eventCount={events.length} />
        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" iconLeft={<Download size={13} />}
                  onClick={() => focusedSnap && exportCsv(focusedSnap, focusedScore)}
                  disabled={!focusedSnap}>Export CSV</Button>
          <Button variant="ghost" size="sm" onClick={() => window.print()}>Print / PDF</Button>
        </div>
      </div>

      {/* Agent tabs + add */}
      <div className="flex items-center gap-2 flex-wrap border-b border-v2-border-subtle pb-2">
        {selected.map((id) => {
          const snap = snapshots[id];
          const tier = snap?.tier || 'Unrated';
          const sc = liveScores[id] != null ? liveScores[id] : (snap?.score != null ? Number(snap.score) : null);
          const active = focused === id;
          return (
            <span key={id}
                  className={`inline-flex items-center gap-2 text-[12px] px-2.5 py-1 rounded-v2-md cursor-pointer transition-colors ${active ? 'bg-v2-action-soft' : 'hover:bg-v2-muted/50'}`}
                  onClick={() => setFocused(id)}>
              {/* The dot sits beside a score, so it carries the score's band — not the tier. */}
              <span style={{ width: 8, height: 8, borderRadius: 999, background: publishedBandStyle(sc).color }} />
              <span className={active ? 'font-semibold text-v2-action-strong' : 'text-v2-ink'}>
                {nameOf(id)}{sc != null ? ` · ${displayScore(sc)}` : ''}
              </span>
              <X size={12} className="text-v2-ink-muted hover:text-v2-danger-strong" onClick={(e) => { e.stopPropagation(); removeAgent(id); }} />
            </span>
          );
        })}
        {selected.length < MAX_AGENTS && (
          <div className="flex items-center gap-1.5">
            <AgentPickerDropdown
              label="+ Add agent…"
              minWidth={170}
              items={selectableAgents.map((a) => ({
                id: String(a.id),
                name: a.name,
                sub: a.archetype || a.type || '',
              }))}
              onPick={(id, name) => addAgent(id, name)}
            />
            {topActiveItems.length > 0 && (
              <AgentPickerDropdown
                label="Top 10 active…"
                minWidth={150}
                pageSize={10}
                items={topActiveItems}
                onPick={(id, name) => addAgent(id, name)}
              />
            )}
          </div>
        )}
        <span className="text-[10.5px] text-v2-ink-muted ml-1">{selected.length} of {MAX_AGENTS}</span>
      </div>

      {/* Hero: ring + ladder + signals for the focused agent */}
      {focused == null ? (
        <Card padding="lg"><p className="text-[12.5px] text-v2-ink-muted text-center py-6 m-0">Select an agent to watch its live Trust Score.</p></Card>
      ) : focusedSnap === undefined ? (
        <Card padding="lg"><p className="text-[12.5px] text-v2-ink-muted text-center py-6 m-0">Loading trust snapshot…</p></Card>
      ) : focusedSnap === null ? (
        <Card padding="lg"><p className="text-[12.5px] text-v2-danger-strong text-center py-6 m-0">Could not load trust snapshot for {nameOf(focused)}.</p></Card>
      ) : (
        <Card padding="md">
          {/* The Crossing — Slice E. Conformance-scored autonomy state. */}
          {focusedSnap.autonomyState && (
            <div className="mb-3 flex items-center gap-2 flex-wrap text-[11.5px]">
              {focusedSnap.autonomyState === 'SCORED_AUTONOMOUS' ? (
                <span className="px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: 'var(--v2-band-strong-soft)',
                               color: 'var(--v2-band-strong-ink)' }}>
                  Scored autonomous
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: 'var(--v2-band-limited-soft)',
                               color: 'var(--v2-band-limited-ink)' }}>
                  Scored standby
                </span>
              )}
              <span className="text-v2-ink-muted">
                {focusedSnap.autonomyState === 'SCORED_AUTONOMOUS'
                  ? 'Graduated — the cognitive path runs for real under the trust gate.'
                  : `Earning autonomy in shadow — ${focusedSnap.conformanceSamples}/${focusedSnap.conformanceSampleTarget} samples · mean ${focusedSnap.conformanceMean != null ? Math.round(Number(focusedSnap.conformanceMean) * 100) : 0}% (need ${focusedSnap.requiredConformance != null ? Math.round(Number(focusedSnap.requiredConformance)) : 90}%).`}
              </span>
            </div>
          )}
          <div className="grid md:grid-cols-[200px_1fr] gap-5 items-center">
            <TrustRing score={focusedScore} tier={focusedSnap.tier} name={focusedSnap.agentName} />
            <div>
              <AutonomyLadder snap={focusedSnap} />
              <ScoreInputs snap={focusedSnap} />
            </div>
          </div>
        </Card>
      )}

      {/* Live execution feed */}
      <Card padding="none" className="flex flex-col" style={{ maxHeight: 'calc(100vh - 560px)', minHeight: 240 }}>
        <div className="px-4 py-2.5 border-b border-v2-border-subtle bg-v2-surface-muted flex items-center gap-3">
          <Radio size={13} className={status === 'connected' ? 'text-v2-success-strong' : 'text-v2-ink-muted'} />
          <span className="text-[12.5px] font-semibold text-v2-ink">Live execution{focused ? ` · ${nameOf(focused)}` : ''}</span>
          <span className="text-[10.5px] text-v2-ink-muted">6-layer policy pipeline · conformance scored per run</span>
          <Button variant={autoScroll ? 'secondary' : 'ghost'} size="sm" onClick={() => setAutoScroll((v) => !v)} className="ml-auto">
            Auto-scroll: {autoScroll ? 'On' : 'Off'}
          </Button>
        </div>
        <div ref={feedRef} className="overflow-y-auto flex-1 p-3">
          {feedEvents.length === 0 ? (
            <div className="text-center py-10">
              <Radio size={26} className={`mx-auto mb-2 ${status === 'connected' ? 'text-v2-success-strong' : 'text-v2-ink-muted'}`} />
              <p className="text-[12.5px] text-v2-ink-muted m-0">
                {status === 'connected' ? 'Waiting for live executions from the selected agents…' : 'Not connected to the execution stream.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2 m-0 p-0 list-none">
              {feedEvents.map((e) => <ExecCard key={e.id} event={e} agentName={nameOf(e.agentId)} />)}
            </ul>
          )}
        </div>
      </Card>
    </div>
  );
}

/* ───── Trust Score ring (Prove7 Agent Trust Score™) ───── */
function TrustRing({ score, tier, name }) {
  /* The ring shows the SCORE, so it carries the score's band — the one verdict a number gets, the
   * same word and colour it has on every other surface.
   *
   * It used to be labelled and coloured by the autonomy tier instead. That put "Restricted" in
   * orange exactly where every other screen puts the band, so one agent read green Adequate on its
   * summary and orange Restricted here, off the same number. Autonomy is not a second opinion on
   * the score; it is what the score permits, and it is shown as that below. */
  const style = publishedBandStyle(score);
  const t = tier || 'Unrated';
  const color = style.color;
  const ink = style.ink;
  const r = 52, C = 2 * Math.PI * r;
  const rated = score != null;
  const shown = displayScore(score);
  const pct = rated ? Math.max(0, Math.min(100, score)) : 0;
  const dash = (C * pct) / 100;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <svg viewBox="0 0 120 120" width="100%" style={{ maxWidth: 168, display: 'block' }} role="img"
           aria-label={`Trust score ${rated ? shown : 'unrated'}, ${style.label}, permitting ${t} autonomy`}>
        <circle cx="60" cy="60" r={r} fill="none" stroke="var(--v2-bg-muted)" strokeWidth="11" />
        {rated && (
          <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="11" strokeLinecap="round"
                  strokeDasharray={`${dash.toFixed(1)} ${(C - dash).toFixed(1)}`} transform="rotate(-90 60 60)" />
        )}
        {/* Number + /100 only — keep them centred in the ring. */}
        <text x="60" y="62" textAnchor="middle"
              fontSize="var(--v2-trust-score-size)"
              fontWeight="var(--v2-trust-score-weight)"
              letterSpacing="var(--v2-trust-score-tracking)"
              fill={ink}>
          {rated ? shown : '—'}
        </text>
        <text x="60" y="80" textAnchor="middle" fontSize="var(--v2-trust-meta-size)"
              fill="var(--v2-text-muted)">/100</text>
      </svg>
      {/* The band is the verdict and takes the band colour. The autonomy it permits follows as a
          consequence, in neutral type, so the two can never be read as competing answers. */}
      <div className="font-semibold mt-1"
           style={{ color: ink, fontSize: 'var(--v2-trust-row-size)' }}>{style.label}</div>
      {rated && (
        <div className="text-[11px] text-v2-ink-secondary mt-0.5">Permits {t} autonomy</div>
      )}
      <div className="text-[11px] text-v2-ink-muted mt-0.5">Prove7 Agent Trust Score™</div>
    </div>
  );
}

/* ───── Autonomy ladder (real thresholds) ───── */
function AutonomyLadder({ snap }) {
  const rows = [
    ['Self-Autonomous', snap.selfAutonomousThreshold, TIER_COLOR['Self-Autonomous'], `≥ ${fmt(snap.selfAutonomousThreshold)}`],
    ['Supervised', snap.supervisedThreshold, TIER_COLOR['Supervised'], `${fmt(snap.supervisedThreshold)} – ${fmt(snap.selfAutonomousThreshold)}`],
    ['Restricted', snap.restrictedThreshold, TIER_COLOR['Restricted'], `${fmt(snap.restrictedThreshold)} – ${fmt(snap.supervisedThreshold)}`],
    ['Probation', 0, TIER_COLOR['Probation'], `< ${fmt(snap.restrictedThreshold)}`],
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10.5px] font-mono-v2 tracking-wider text-v2-ink-muted">AUTONOMY THRESHOLDS</span>
        <span className="text-[10px] text-v2-ink-muted">from Trust → Scoring</span>
      </div>
      {rows.map(([label, , color, range]) => {
        const cur = snap.tier === label;
        return (
          <div key={label} className={`flex items-center justify-between py-1 text-[12px] ${cur ? 'bg-v2-action-soft rounded-v2-sm px-1' : ''}`} style={{ borderBottom: '0.5px solid var(--v2-border-subtle)' }}>
            <span className={cur ? 'font-semibold text-v2-action-strong' : 'text-v2-ink'}>
              <span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: 3, background: color, verticalAlign: -1, marginRight: 7 }} />
              {label}{cur ? ' · current' : ''}
            </span>
            <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{range}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ───── Score inputs (real config + conformance aggregate) ───── */
function ScoreInputs({ snap }) {
  const mean = snap.conformanceMean != null ? Number(snap.conformanceMean) : null;
  const req = snap.requiredConformance != null ? Number(snap.requiredConformance) : null;
  const items = [
    ['Conformance', mean != null ? mean.toFixed(2) : '—', mean != null ? mean * 100 : 0, 'var(--v2-band-adequate)',
      `${req != null ? '≥ ' + (req / 100).toFixed(2) + ' req · ' : ''}N ${snap.conformanceSamples}/${snap.conformanceSampleTarget} · wt ${fmt(snap.conformanceWeight)} · ${snap.conformanceComparatorMode}${snap.shadowConformanceEnabled ? ' · shadow on' : ' · shadow off'}`],
    ['Weighted violations', fmt(snap.weightedViolations), Math.min(100, Number(snap.weightedViolations || 0) * 5), 'var(--v2-band-critical)',
      `${snap.blockCount} blk · ${snap.warnCount} warn · ${snap.auditCount} audit`],
    ['Evaluations', String(snap.totalEvaluations), Math.min(100, (snap.totalEvaluations || 0)), 'var(--v2-band-adequate)', `over ${snap.windowDays}d window`],
    ['Score floor', fmt(snap.scoreFloor), Number(snap.scoreFloor || 0), 'var(--v2-band-unrated)', 'probation never below floor'],
  ];
  return (
    <div className="mt-3">
      <div className="text-[10.5px] font-mono-v2 tracking-wider text-v2-ink-muted mb-1.5">SCORE INPUTS · THIS WINDOW</div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        {items.map(([label, val, pct, color, sub]) => (
          <div key={label} className="text-[11px]">
            <div className="flex justify-between"><span className="text-v2-ink-secondary">{label}</span><span className="font-semibold">{val}</span></div>
            <div style={{ height: 4, background: 'var(--v2-bg-muted)',
              borderRadius: 2, marginTop: 3 }}>
              <div style={{ height: 4, width: `${Math.max(3, pct)}%`, background: color, borderRadius: 2 }} />
            </div>
            <div className="text-[10px] text-v2-ink-muted mt-0.5 font-mono-v2">{sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── Feed card ───── */
function ExecCard({ event, agentName }) {
  const [expanded, setExpanded] = useState(false);
  const decision = (event.decision || 'EXECUTING').toUpperCase();
  const isExec = decision === 'EXECUTING';
  const isBlocked = decision === 'BLOCKED';
  const isWarned = decision === 'WARNED' || (event.warnings && event.warnings > 0);
  const border = isBlocked ? 'border-l-v2-danger' : isWarned ? 'border-l-v2-warning' : isExec ? 'border-l-v2-action' : 'border-l-v2-success';
  return (
    <li className={`bg-v2-surface border border-v2-border-subtle border-l-4 ${border} rounded-v2-md overflow-hidden`}>
      <button onClick={() => setExpanded((v) => !v)} className="w-full text-left px-3 py-2.5 hover:bg-v2-surface-muted/40 grid items-center gap-2"
              style={{ gridTemplateColumns: 'auto 1fr auto auto auto auto' }}>
        {isExec ? <Loader2 size={16} className="text-v2-warning-strong animate-spin" /> : isBlocked ? <XCircle size={16} className="text-v2-danger-strong" /> : <CheckCircle2 size={16} className="text-v2-success-strong" />}
        <div className="min-w-0">
          <div className="font-semibold text-v2-ink text-[12.5px] truncate">
            {agentName} · {event.type === 'mcp' ? `mcp:${event.toolName || 'tool'}` : event.type === 'prove' ? `prove sealed · ${event.prove || event.skillName}` : `governed_execute · ${event.skillName || 'skill'}`}
          </div>
          <div className="text-[10.5px] text-v2-ink-muted truncate">{String(event.id).slice(0, 16)}</div>
        </div>
        <PolicyPipeline stages={event.stages} type={event.type} variant="compact" currentIndex={isExec ? event.stages.length : null} />
        <span className="text-[10.5px] font-mono-v2 text-v2-ink-muted">{event.conformance != null ? `conf ${Number(event.conformance).toFixed(2)}` : ''}</span>
        <DecisionBadge decision={decision} />
        <span className="text-[10.5px] font-mono-v2 text-v2-ink-muted">{event.duration != null ? `${event.duration}ms` : '—'}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-v2-border-subtle bg-v2-surface-muted/30 text-[11.5px]">
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-2">
            <Field label="Execution ID" value={event.id} />
            <Field label="Agent" value={agentName} />
            <Field label="Decision" value={event.decision} />
            <Field label="Conformance" value={event.conformance != null ? Number(event.conformance).toFixed(3) : null} />
            <Field label="Duration" value={event.duration != null ? `${event.duration} ms` : null} />
            <Field label="Stages" value={event.stages?.length || 0} />
          </div>
        </div>
      )}
    </li>
  );
}

function DecisionBadge({ decision }) {
  const tone = decision === 'ALLOWED' ? 'success' : decision === 'WARNED' ? 'warning' : decision === 'BLOCKED' ? 'danger' : decision === 'EXECUTING' ? 'warning' : 'muted';
  return <Pill tone={tone} size="sm" dot={tone !== 'muted'}>{decision}</Pill>;
}

function Field({ label, value }) {
  if (value == null || value === '') return null;
  return (<><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider inline">{label}: </dt><dd className="text-v2-ink m-0 inline font-mono-v2 text-[10.5px]">{String(value)}</dd></>);
}

/* ───── Helpers ───── */
function fmt(v) { if (v == null) return '—'; const n = Number(v); return Number.isInteger(n) ? String(n) : n.toFixed(2); }

function exportCsv(snap, score) {
  const rows = [
    ['Agent', snap.agentName], ['Archetype', snap.archetype], ['Trust score', score != null ? Math.round(score) : ''],
    ['Tier', snap.tier], ['Recommended autonomy', snap.recommendedAutonomy],
    ['Conformance (mean)', snap.conformanceMean], ['Conformance samples', `${snap.conformanceSamples}/${snap.conformanceSampleTarget}`],
    ['Required conformance', snap.requiredConformance], ['Conformance weight', snap.conformanceWeight],
    ['Comparator mode', snap.conformanceComparatorMode], ['Shadow enabled', snap.shadowConformanceEnabled],
    ['Weighted violations', snap.weightedViolations], ['Blocks/Warns/Audits', `${snap.blockCount}/${snap.warnCount}/${snap.auditCount}`],
    ['Evaluations', snap.totalEvaluations], ['Score floor', snap.scoreFloor], ['Window (days)', snap.windowDays],
  ];
  const csv = rows.map((r) => r.map((v) => `"${v ?? ''}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `live-trust-${snap.agentName || 'agent'}.csv`; a.click();
  URL.revokeObjectURL(url);
}

function boundedAppend(list, item, max = 200) {
  const next = [...list, item];
  if (next.length > max) next.splice(0, next.length - max);
  return next;
}

function parseSseChunk(chunk) {
  if (!chunk || !chunk.trim()) return null;
  const lines = chunk.split('\n');
  let event = 'message';
  const dataLines = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
  }
  let data = dataLines.join('\n');
  try { data = JSON.parse(data); } catch (_) { /* keep raw */ }
  return { event, data };
}

export default LiveExecutionTab;
